// components/AssignTestsDialog.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Checkbox,
    Grid,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    CircularProgress,
    RadioGroup,
    FormControlLabel,
    Radio,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import * as AssignTestsService from "../../services/AssignTestsService";

const AssignTestsDialog = ({
    open,
    onClose,
    itemType = "Sample", // "Sample" | "Quote" | "Order" | "Slate" | "Process"
    itemLabel = "Sample:",
    itemValue = "",
    matrix = "All",
    onAssignComplete,
}) => {
    // State management
    const [categories, setCategories] = useState([]);
    const [tests, setTests] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("(All Categories)");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTests, setSelectedTests] = useState([]);
    const [selectAllTests, setSelectAllTests] = useState(false);
    const [loading, setLoading] = useState(false);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertSeverity, setAlertSeverity] = useState("info");
    const [sortColumn, setSortColumn] = useState("parameter");

    const searchInputRef = useRef(null);

    // Load categories on mount - Matches VB.NET ShowTestCats
    useEffect(() => {
        if (open) {
            loadCategories();
        }
    }, [open]);

    const loadCategories = async () => {
        setCategoriesLoading(true);
        try {
            const data = await AssignTestsService.GetTestCategories();
            const categoryList = data.results || data || [];
            
            // Add "(All Categories)" at the beginning if not present
            const hasAllCategories = categoryList.some(
                c => c.testCategory === "(All Categories)"
            );
            
            if (!hasAllCategories) {
                categoryList.unshift({ testCategory: "(All Categories)" });
            }
            
            setCategories(categoryList);
        } catch (error) {
            console.error("Error loading categories:", error);
            setAlertMessage("Failed to load categories");
            setAlertSeverity("error");
            NotificationManager.error("Failed to load categories", "Error");
        } finally {
            setCategoriesLoading(false);
        }
    };

    // Load tests when search or category changes - Matches VB.NET SelectTests
    const loadTests = async () => {
        // Don't search if query is empty and category is "All Categories"
        if (!searchQuery.trim() && selectedCategory === "(All Categories)") {
            setTests([]);
            return;
        }

        setLoading(true);
        try {
            const data = await AssignTestsService.SearchTests({
                queryArg: searchQuery,
                category: selectedCategory,
                matrix: matrix,
                sortBy: sortColumn
            });

            setTests(data.results || data || []);
            setAlertMessage("");
            
            // Clear selections when results change
            setSelectedTests([]);
            setSelectAllTests(false);
        } catch (error) {
            console.error("Error loading tests:", error);
            setAlertMessage("Failed to load tests");
            setAlertSeverity("error");
            NotificationManager.error("Failed to load tests", "Error");
        } finally {
            setLoading(false);
        }
    };

    // Debounced search - Matches VB.NET tbTestQueryArg AutoPostBack
    useEffect(() => {
        if (!open) return;

        const timer = setTimeout(() => {
            loadTests();
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery, selectedCategory, sortColumn, open]);

    // Handle category change - Matches VB.NET rblCategories_SelectedIndexChanged
    const handleCategoryChange = (event) => {
        setSelectedCategory(event.target.value);
        setSearchQuery(""); // Clear search when category changes (matches VB.NET)
    };

    // Handle column sort - Matches VB.NET gvTests_Sortcommand
    const handleSort = (column) => {
        setSortColumn(column);
    };

    // Handle test selection - Matches VB.NET cbSelect checkbox
    const handleTestCheckbox = (testCode) => {
        setSelectedTests((prev) => {
            const newSelection = prev.includes(testCode)
                ? prev.filter((code) => code !== testCode)
                : [...prev, testCode];

            setSelectAllTests(newSelection.length === tests.length && tests.length > 0);
            return newSelection;
        });
    };

    // Handle select all - Matches VB.NET CheckUncheckAll function
    const handleSelectAllTests = () => {
        if (selectAllTests) {
            setSelectedTests([]);
        } else {
            setSelectedTests(tests.map((t) => t.testCode));
        }
        setSelectAllTests(!selectAllTests);
    };

    // Assign tests - Matches VB.NET AddTests with postbackcode logic
    const assignTests = async (closeAfter = false) => {
        if (selectedTests.length === 0) {
            // Matches VB.NET: "No Tests Selected"
            setAlertMessage("No Tests Selected");
            setAlertSeverity("warning");
            NotificationManager.warning("No Tests Selected", "Warning");
            return;
        }

        setLoading(true);
        try {
            const result = await AssignTestsService.AssignTests({
                itemType: itemType,
                itemValue: itemValue,
                testCodes: selectedTests
            });

            // Let the parent finish first: in Sample mode it saves the tests,
            // so a failed save lands in the catch below instead of a success.
            if (onAssignComplete) {
                await onAssignComplete({
                    itemType,
                    itemValue,
                    testCodes: selectedTests,
                    assignedTests: result.assignedTests || result,
                    assignedCount: result.assignedCount
                });
            }

            setAlertMessage(result.message);
            setAlertSeverity("success");
            NotificationManager.success(result.message, "Success");

            // Clear selections after assignment (matches VB.NET: TestCheckbox.Checked = False)
            setSelectedTests([]);
            setSelectAllTests(false);

            // Matches VB.NET vPostbackcode = 1 (close) vs 2 (stay open)
            if (closeAfter) {
                setTimeout(() => onClose(), 1500);
            }
        } catch (error) {
            console.error("Error assigning tests:", error);
            const errorMsg = error.message || "Failed to assign tests";
            setAlertMessage(errorMsg);
            setAlertSeverity("error");
            NotificationManager.error(errorMsg, "Error");
        } finally {
            setLoading(false);
        }
    };

    // Reset on open - Matches VB.NET Page_Load Not IsPostBack
    useEffect(() => {
        if (open) {
            setSearchQuery("");
            setSelectedCategory("(All Categories)");
            setTests([]);
            setSelectedTests([]);
            setSelectAllTests(false);
            setAlertMessage("");
            setAlertSeverity("info");
            setLoading(false);
            setSortColumn("parameter");
        }
    }, [open]);

    // Focus search on open
    useEffect(() => {
        if (open && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: "90vw",
                    height: "90vh",
                    maxWidth: "90vw",
                    maxHeight: "90vh",
                    margin: "2.5vh auto",
                    display: "flex",
                    flexDirection: "column",
                },
            }}
        >
            {/* Title - matches VB.NET bgcolor="#005fa9" */}
            <DialogTitle sx={{ bgcolor: "#005FA9", color: "white", p: 1.5 }}>
                <Typography variant="h6" sx={{ fontSize: "12pt", fontWeight: "bold" }}>
                    Select and Assign Lab Tests
                </Typography>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    p: 0,
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflow: "hidden",
                }}
            >
                {/* Alert message - matches VB.NET lblAlert */}
                {alertMessage && (
                    <Alert severity={alertSeverity} sx={{ m: 2, mb: 0 }}>
                        {alertMessage}
                    </Alert>
                )}

                <Grid container sx={{ flexGrow: 1, height: "100%", overflow: "hidden" }}>
                    {/* Left Panel - Tests (matches VB.NET left column) */}
                    <Grid
                        item
                        xs={8}
                        sx={{
                            borderRight: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        {/* Search box - matches VB.NET bgcolor="#ece9d8" */}
                        <Box sx={{ p: 2, bgcolor: "#ece9d8", borderBottom: "1px solid #ccc" }}>
                            <Typography variant="body2" sx={{ fontSize: "9pt", mb: 1, fontWeight: "bold" }}>
                                Test Search:
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Enter Test Search Expression"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                inputRef={searchInputRef}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, fontSize: "1.2rem" }} />,
                                    sx: { fontSize: "9pt" },
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "white",
                                    },
                                }}
                            />
                        </Box>

                        {/* Tests Grid - matches VB.NET gvTests with scrollable div */}
                        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                            {loading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : tests.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: "center" }}>
                                    <Typography variant="body2" sx={{ fontSize: "9pt", fontWeight: "bold" }}>
                                        {searchQuery.trim() || selectedCategory !== "(All Categories)"
                                            ? "No test records found"
                                            : "Enter search criteria or select a category"}
                                    </Typography>
                                </Box>
                            ) : (
                                <TableContainer>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {/* Sortable columns - matches VB.NET AllowSorting="True" */}
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "#005FA9",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "8pt",
                                                        padding: "4px 8px",
                                                        cursor: "pointer",
                                                        "&:hover": { bgcolor: "#004a8a" }
                                                    }}
                                                    onClick={() => handleSort("test_code")}
                                                >
                                                    Test Code {sortColumn === "test_code" && "▼"}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "#005FA9",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "8pt",
                                                        padding: "4px 8px",
                                                        cursor: "pointer",
                                                        "&:hover": { bgcolor: "#004a8a" }
                                                    }}
                                                    onClick={() => handleSort("parameter")}
                                                >
                                                    Parameter {sortColumn === "parameter" && "▼"}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "#005FA9",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "8pt",
                                                        padding: "4px 8px",
                                                        cursor: "pointer",
                                                        "&:hover": { bgcolor: "#004a8a" }
                                                    }}
                                                    onClick={() => handleSort("test_units")}
                                                >
                                                    Units {sortColumn === "test_units" && "▼"}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "#005FA9",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "8pt",
                                                        padding: "4px 8px",
                                                        cursor: "pointer",
                                                        "&:hover": { bgcolor: "#004a8a" }
                                                    }}
                                                    onClick={() => handleSort("method_code")}
                                                >
                                                    Method {sortColumn === "method_code" && "▼"}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "#005FA9",
                                                        color: "white",
                                                        fontWeight: "bold",
                                                        fontSize: "8pt",
                                                        padding: "4px 8px",
                                                        textAlign: "center",
                                                        width: "60px",
                                                    }}
                                                >
                                                    {/* Select All checkbox - matches VB.NET cbSelectUnselectAll */}
                                                    <Checkbox
                                                        checked={selectAllTests}
                                                        onChange={handleSelectAllTests}
                                                        sx={{ color: "white", padding: 0 }}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {tests.map((test, index) => (
                                                <TableRow
                                                    key={test.testCode}
                                                    sx={{
                                                        // Matches VB.NET AlternatingRowStyle BackColor="GhostWhite"
                                                        bgcolor: index % 2 === 1 ? "ghostwhite" : "white",
                                                    }}
                                                >
                                                    <TableCell sx={{ fontSize: "8pt", padding: "4px 8px" }}>
                                                        {test.testCode}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: "8pt", padding: "4px 8px" }}>
                                                        {test.parameter}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: "8pt", padding: "4px 8px" }}>
                                                        {test.testUnits}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: "8pt", padding: "4px 8px" }}>
                                                        {test.methodCode}
                                                    </TableCell>
                                                    <TableCell sx={{ padding: "4px 8px", textAlign: "center" }}>
                                                        {/* Individual test checkbox - matches VB.NET cbSelect */}
                                                        <Checkbox
                                                            checked={selectedTests.includes(test.testCode)}
                                                            onChange={() => handleTestCheckbox(test.testCode)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Grid>

                    {/* Right Panel - Categories (matches VB.NET right column) */}
                    <Grid
                        item
                        xs={4}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        {/* Header - matches VB.NET bgcolor="#ece9d8" */}
                        <Box sx={{ p: 2, bgcolor: "#ece9d8", borderBottom: "1px solid #ccc" }}>
                            <Typography variant="body2" sx={{ fontSize: "9pt" }}>
                                Test Categories
                            </Typography>
                        </Box>

                        {/* Categories Radio List - matches VB.NET rblCategories with scrollable div */}
                        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
                            {categoriesLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                <RadioGroup
                                    value={selectedCategory}
                                    onChange={handleCategoryChange}
                                >
                                    {categories.map((category) => (
                                        <FormControlLabel
                                            key={category.testCategory}
                                            value={category.testCategory}
                                            control={<Radio size="small" />}
                                            label={
                                                <Typography sx={{ fontSize: "9pt" }}>
                                                    {category.testCategory}
                                                </Typography>
                                            }
                                            sx={{ mb: 0.5 }}
                                        />
                                    ))}
                                </RadioGroup>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            {/* Footer - matches VB.NET bottom row bgcolor="gainsboro" */}
            <DialogActions
                sx={{
                    px: 3,
                    py: 1.5,
                    bgcolor: "gainsboro",
                    justifyContent: "space-between",
                    borderTop: "1px solid #ccc",
                }}
            >
                <Box>
                    {/* Item information display - matches VB.NET tbItemvalue and tbSample_matrix */}
                    <Typography
                        variant="body2"
                        component="span"
                        sx={{ fontWeight: "bold", fontSize: "9pt" }}
                    >
                        {itemLabel}
                    </Typography>
                    <Typography
                        variant="body2"
                        component="span"
                        sx={{ ml: 1, fontSize: "9pt" }}
                    >
                        {itemValue}
                    </Typography>
                    <Typography
                        variant="body2"
                        component="span"
                        sx={{ ml: 2, fontSize: "9pt" }}
                    >
                        Sample Matrix:
                    </Typography>
                    <Typography
                        variant="body2"
                        component="span"
                        sx={{ ml: 1, fontSize: "9pt" }}
                    >
                        {matrix}
                    </Typography>
                </Box>
                <Box>
                    <Button
                        onClick={onClose}
                        sx={{ mr: 1, fontSize: "9pt" }}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    {/* cmdAssign - Add Items (vPostbackcode = 2, stays open) */}
                    <Button
                        variant="contained"
                        onClick={() => assignTests(false)}
                        disabled={loading || selectedTests.length === 0}
                        sx={{ mr: 1, fontSize: "9pt" }}
                        title="Click here to assign selected tests"
                    >
                        Add Items
                    </Button>
                    {/* cmdAddandExit - Add Items and Exit (vPostbackcode = 1, closes) */}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => assignTests(true)}
                        disabled={loading || selectedTests.length === 0}
                        sx={{ fontSize: "9pt" }}
                        title="Click here to assign selected tests and close this page"
                    >
                        Add Items and Exit
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default AssignTestsDialog;